import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Loader2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function QuoteRespond() {
  const { bookingId } = useParams();
  const [search] = useSearchParams();
  const token = search.get("t") || "";
  const preset = search.get("action");
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  useEffect(() => {
    api.get(`/bookings/${bookingId}/quote`, { params: { t: token } }).then((r) => setCtx(r.data)).catch((e) => setError(e?.response?.data?.detail || "Invalid quote link."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, token]);

  const respond = async (action) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/bookings/${bookingId}/quote/respond`, { action, token });
      setResult(data.quote_status);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not record your answer.");
    } finally {
      setBusy(false);
    }
  };

  const status = result || ctx?.quote_status;

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200">
      <Navbar />
      <section className="mx-auto max-w-xl px-4 py-16" data-testid="quote-page">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Your quote</div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-slate-100">{ctx?.service_name || "Quote"}</h1>
        <div className="cj-card p-6 mt-8">
          {!ctx && !error && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          {ctx && (
            <>
              <div className="font-mono text-xs text-slate-500">{ctx.vehicle_label} · {ctx.scheduled_date} · {ctx.scheduled_time}</div>
              <div className="mt-4 flex items-end justify-between">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Quoted price</div>
                <div className="font-display font-extrabold text-4xl text-[#ff6b00]" data-testid="quote-price">${ctx.quoted_price?.toFixed(2)}</div>
              </div>
              {ctx.quote_note && <p className="mt-3 text-sm text-slate-300 border-l-2 border-[#ff6b00]/40 pl-3">{ctx.quote_note}</p>}
              <div className="mt-2 text-xs text-slate-500">Your ${ctx.deposit_amount} deposit {ctx.payment_status === "paid" ? "is already paid and" : ""} comes off this price.</div>

              {status === "sent" ? (
                <div className="mt-6 flex gap-2">
                  <button disabled={busy} onClick={() => respond("accept")} data-testid="quote-accept-button" className={`cj-btn-primary flex-1 px-4 py-3 rounded-full inline-flex items-center justify-center gap-2 ${preset === "accept" ? "ring-2 ring-[#ff6b00]/50" : ""}`}><Check className="h-4 w-4" /> Accept quote</button>
                  <button disabled={busy} onClick={() => respond("decline")} data-testid="quote-decline-button" className="flex-1 px-4 py-3 rounded-full text-red-300 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center justify-center gap-2"><X className="h-4 w-4" /> Decline</button>
                </div>
              ) : (
                <div className={`mt-6 p-4 rounded-xl border text-sm ${status === "accepted" ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200" : "border-white/10 bg-white/5 text-slate-300"}`} data-testid="quote-result">
                  {status === "accepted" ? "Quote accepted — CJ will see you on the day. Thanks!" : "You declined this quote. CJ has been notified and may reach out."}
                </div>
              )}
            </>
          )}
          {error && <div className="mt-4 text-sm text-red-400 font-mono uppercase tracking-wider" data-testid="quote-error">{error}</div>}
        </div>
        <Link to="/my-bookings" className="mt-6 inline-block text-sm text-slate-400 hover:text-slate-200">Manage my bookings →</Link>
      </section>
      <Footer />
    </div>
  );
}
